'use client';

import { useState, useRef, useEffect } from 'react';
import { Upload, X, Camera, AlertTriangle, CheckCircle, Smartphone, Info, ChevronRight, Store, ShieldCheck, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import LoginModal from './LoginModal';
import { useLanguage } from '../contexts/LanguageContext';
import { jsPDF } from 'jspdf';
import * as htmlToImage from 'html-to-image';
import { getT } from '@/utils/translations';

// Helper for classes
function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

const PHOTO_GUIDES = {
    BAG: {
        icon: '👜',
        label: { FR: 'Sac', EN: 'Bag' },
        steps: [
            { FR: "Vue de face complète", EN: "Full front view" },
            { FR: "Logo ou estampille (Gros plan)", EN: "Brand stamp or logo (Close-up)" },
            { FR: "Code date / Numéro de série", EN: "Date code / Serial number" },
            { FR: "Détail des coutures", EN: "Stitching detail" },
            { FR: "Fermeture ou Bouclerie", EN: "Hardware or Zipper" }
        ]
    },
    WATCH: {
        icon: '⌚',
        label: { FR: 'Montre', EN: 'Watch' },
        steps: [
            { FR: "Cadran complet (Vue de face)", EN: "Full dial (Front view)" },
            { FR: "Dos du boîtier", EN: "Case back" },
            { FR: "Couronne / Boutons", EN: "Crown / Pushers" },
            { FR: "Fermoir de bracelet", EN: "Clasp / Buckle" },
            { FR: "Numéro de série gravé", EN: "Engraved Serial number" }
        ]
    },
    SNEAKERS: {
        icon: '👟',
        label: { FR: 'Sneakers', EN: 'Sneakers' },
        steps: [
            { FR: "Vue de profil extérieure", EN: "Outer profile view" },
            { FR: "Étiquette de taille intérieure", EN: "Inner size tag" },
            { FR: "Coutures sous la semelle", EN: "Insole stitching" },
            { FR: "Semelle extérieure", EN: "Outsole / Tread" },
            { FR: "Talon (Back tab)", EN: "Heel / Back tab" }
        ]
    },
    CLOTHING: {
        icon: '👕',
        label: { FR: 'Vêtement', EN: 'Clothing' },
        steps: [
            { FR: "Vue d'ensemble", EN: "Full front view" },
            { FR: "Étiquette de marque (Cou)", EN: "Brand neck tag (Close-up)" },
            { FR: "Étiquette de lavage interne", EN: "Wash/Care tag" },
            { FR: "Détail bouton ou zip", EN: "Button or zipper detail" },
            { FR: "Coutures / Broderies logo", EN: "Logo Embroidery / Stitching" }
        ]
    }
};

export default function VisionUpload() {
    const [files, setFiles] = useState<File[]>([]);
    const [status, setStatus] = useState<'IDLE' | 'ANALYZING' | 'SUCCESS' | 'ERROR'>('IDLE');
    const [result, setResult] = useState<any>(null);
    const { lang, setLang } = useLanguage();
    const [category, setCategory] = useState<'BAG' | 'WATCH' | 'SNEAKERS' | 'CLOTHING'>('BAG');
    const [brandName, setBrandName] = useState('');
    const [modelName, setModelName] = useState('');

    // Auth State
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [userProfile, setUserProfile] = useState<any>(null);
    const [checkWatchRegister, setCheckWatchRegister] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const reportRef = useRef<HTMLDivElement>(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('vision_token');
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload.exp * 1000 < Date.now()) {
                    localStorage.removeItem('vision_token');
                    setIsLoggedIn(false);
                    setUserProfile(null);
                } else {
                    setIsLoggedIn(true);
                    fetchUserProfile(token);
                    const timeUntilExpiry = (payload.exp * 1000) - Date.now();
                    const timer = setTimeout(() => {
                        localStorage.removeItem('vision_token');
                        setIsLoggedIn(false);
                        setUserProfile(null);
                        alert(getT('sessionExpired', lang));
                        setShowLoginModal(true);
                    }, timeUntilExpiry);
                    return () => clearTimeout(timer);
                }
            } catch (e) {
                localStorage.removeItem('vision_token');
                setIsLoggedIn(false);
                setUserProfile(null);
            }
        }
    }, [lang]);

    const fetchUserProfile = async (token: string) => {
        try {
            const { data } = await axios.get('/auth/me', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUserProfile(data);
        } catch (err) {
            console.error("Failed to fetch user profile", err);
        }
    };

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selected = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
            setFiles(prev => {
                const totalFiles = [...prev, ...selected];
                if (totalFiles.length > 10) {
                    alert(getT('maxPhotosAlert', lang));
                    return prev; // Don't add if it exceeds the limit
                }
                return totalFiles;
            });
        }
    };

    const removeFile = (idx: number) => {
        setFiles(prev => prev.filter((_, i) => i !== idx));
    };


    // Image resizing helper
    const resizeImage = (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    // 1600px is the sweet spot: high detail, low weight (~1-2MB max per image)
                    const MAX_WIDTH = 1600;
                    const MAX_HEIGHT = 1600;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    // Use better quality smoothing
                    if (ctx) {
                        ctx.imageSmoothingEnabled = true;
                        ctx.imageSmoothingQuality = 'high';
                        ctx.drawImage(img, 0, 0, width, height);

                        // OOTENTIK Anti-Scam Watermark overlay
                        ctx.save();
                        ctx.translate(width / 2, height / 2);
                        ctx.rotate(-Math.PI / 4.5); // Diagonal rotation
                        
                        // Main big text
                        ctx.font = `900 ${Math.floor(width / 14)}px Arial`;
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.25)'; // Visible but non-obstructive white text
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText('EXPERTISÉ PAR OOTENTIK', 0, 0);

                        // Sub text 
                        ctx.font = `800 ${Math.floor(width / 28)}px Arial`;
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.15)'; 
                        ctx.fillText('PROTECTED BY OOTENTIK VISION AI - OFFICIAL EXAM', 0, Math.floor(width / 12));
                        ctx.fillText('WARNING: CERTIFICATE REQUIRED FOR AUTHENTICITY', 0, -Math.floor(width / 12));
                        
                        ctx.restore();
                    }

                    canvas.toBlob((blob) => {
                        if (blob) {
                            const newFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now(),
                            });
                            resolve(newFile);
                        } else {
                            resolve(file); // Fallback to original
                        }
                    }, 'image/jpeg', 0.85); // 85% is visually lossless but much lighter
                };
                img.onerror = (err) => resolve(file); // Fallback
            };
            reader.onerror = (err) => resolve(file); // Fallback
        });
    };


    const runAnalysis = async () => {
        if (files.length < 5) {
            alert(getT('minPhotosAlert', lang));
            return;
        }

        if (!isLoggedIn) {
            setShowLoginModal(true);
            return;
        }

        setStatus('ANALYZING');
        try {
            // Resize images before upload
            const filesToProcess = await Promise.all(files.map(f => resizeImage(f)));

            const formData = new FormData();
            filesToProcess.forEach(f => formData.append('files', f));
            if (brandName.trim()) formData.append('brandName', brandName);
            if (modelName.trim()) formData.append('modelName', modelName);
            formData.append('category', category);
            formData.append('checkWatchRegister', checkWatchRegister.toString());

            // Because Caddy config routes /vision/* directly to the backend on port 4000 (bypassing Next.js)
            // We just use the clean relative path.
            const apiUrl = '/vision/analyze';

            // Add Authorization header
            const token = localStorage.getItem('vision_token');
            const { data } = await axios.post(apiUrl, formData, {
                timeout: 120000,
                headers: { Authorization: `Bearer ${token}` }
            });
            setResult(data);
            setStatus('SUCCESS');
        } catch (err: any) {
            console.error("Analysis Error:", err);
            setStatus('ERROR');
            
            // Handle expired/invalid token
            if (err.response?.status === 401) {
                alert(getT('sessionExpired', lang));
                localStorage.removeItem('vision_token');
                setIsLoggedIn(false);
                setShowLoginModal(true);
                return;
            }

            const msg = err.response?.data?.message || err.message || getT('unknownError', lang);
            alert(`${getT('errorPrefix', lang)}: ${msg}`);
        }
    };

    const handleLoginSuccess = (token: string, user: any) => {
        localStorage.setItem('vision_token', token);
        setIsLoggedIn(true);
        fetchUserProfile(token);
        // Auto-start analysis after login if files are present
        if (files.length >= 5) {
            runAnalysis();
        }
    };

    const downloadPDF = async () => {
        if (!reportRef.current) return;
        setIsGeneratingPdf(true);
        try {
            const originalBg = reportRef.current.style.backgroundColor;
            reportRef.current.style.backgroundColor = '#f9fafb';
            
            const dataUrl = await htmlToImage.toJpeg(reportRef.current, { 
                quality: 0.95, 
                pixelRatio: 2,
                backgroundColor: '#f9fafb'
            });
            
            reportRef.current.style.backgroundColor = originalBg;
            
            const img = new Image();
            img.src = dataUrl;
            await new Promise((resolve) => { img.onload = resolve; });
            
            // Format PDF as a single long scrollable page perfectly fitting the content
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: [img.width, img.height]
            });
            
            pdf.addImage(dataUrl, 'JPEG', 0, 0, img.width, img.height);
            
            const safeId = result?.id ? String(result.id).slice(-8).toUpperCase() : 'UNKNOWN';
            pdf.save(`OotentikVision_Report_${safeId}.pdf`);
        } catch (err: any) {
            console.error("PDF generation failed", err);
            alert(`${getT('pdfGenerationError', lang)}: ${err.message || err}`);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const getGradeBadge = (grade: string) => {
        if (!grade) return null;
        const colors: any = {
            'S': 'bg-black text-white border-black',
            'A': 'bg-emerald-100 text-emerald-800 border-emerald-200',
            'B': 'bg-blue-100 text-blue-800 border-blue-200',
            'C': 'bg-orange-100 text-orange-800 border-orange-200',
        };
        const labels: any = {
            'S': getT('gradeS', lang),
            'A': getT('gradeA', lang),
            'B': getT('gradeB', lang),
            'C': getT('gradeC', lang)
        };
        return (
            <div className={`px-3 py-1 rounded-lg text-xs font-black tracking-widest uppercase border ${colors[grade] || colors['B']}`}>
                {getT('condition', lang)}: {labels[grade] || grade}
            </div>
        );
    };

    // Result View (V2 Style)
    if (status === 'SUCCESS' && result) {
        return (
            <div className="w-full max-w-md mx-auto space-y-6 pt-6 pb-20 animate-in fade-in slide-in-from-bottom-5 duration-700 px-4">
                
                <div className="flex justify-between items-center px-1">
                    <button onClick={() => { setStatus('IDLE'); setResult(null); setFiles([]); }} className="text-[10px] font-black text-zinc-400 hover:text-black uppercase tracking-widest flex items-center gap-1 transition-colors">
                        ← {getT('back', lang)}
                    </button>
                    <select 
                        value={lang}
                        onChange={(e) => setLang(e.target.value as any)}
                        className="bg-zinc-100 text-zinc-700 font-bold text-[10px] uppercase tracking-widest rounded-full pl-3 pr-2 py-1.5 border border-zinc-200 outline-none cursor-pointer"
                    >
                        <option value="FR">🇫🇷 Français</option>
                        <option value="EN">🇬🇧 English</option>
                        <option value="JP">🇯🇵 日本語</option>
                        <option value="CN">🇨🇳 中文</option>
                        <option value="IT">🇮🇹 Italiano</option>
                        <option value="ES">🇪🇸 Español</option>
                        <option value="KR">🇰🇷 한국어</option>
                        <option value="AR">🇸🇦 العربية</option>
                        <option value="PT">🇵🇹 Português</option>
                    </select>
                </div>

                <div ref={reportRef} className="space-y-6 bg-white pb-6 pt-6 px-4 rounded-[2rem] border border-zinc-100 shadow-[0_4px_40px_rgba(0,0,0,0.04)] mt-2">

                {/* Image Carousel Mockup */}
                <div className="relative w-full aspect-[4/3] bg-zinc-50 rounded-2xl overflow-hidden shadow-sm">
                    {/* Display the FIRST uploaded image as main product shot */}
                    {files.length > 0 && (
                        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/50 via-transparent to-black/10 pointer-events-none" />
                    )}
                    {files.length > 0 && (
                        <img
                            src={URL.createObjectURL(files[0])}
                            className="w-full h-full object-cover"
                            alt="Product"
                        />
                    )}
                    <div className="absolute top-4 right-4 z-20 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-zinc-800 shadow-sm">
                        {files.length} {getT('photos', lang)}
                    </div>
                    {/* Absolute Score Badge for visual impact */}
                    {result.visionScore > 85 && (
                        <div className="absolute bottom-4 right-4 z-20 w-12 h-12 rounded-full bg-white/95 backdrop-blur-md flex items-center justify-center border border-emerald-100 shadow-xl">
                            <ShieldCheck className="w-6 h-6 text-emerald-500" />
                        </div>
                    )}
                </div>

                {/* Main Card */}
                <div className="bg-white rounded-[2rem] p-6 shadow-[0_4px_40px_rgba(0,0,0,0.04)] border border-zinc-100 space-y-6 relative overflow-hidden">

                    {/* Security Code QR */}
                    {result.id && (
                        <div className="absolute top-6 right-6 flex flex-col items-center justify-center p-2 rounded-xl border border-zinc-100 shadow-[0_4px_20px_rgba(0,0,0,0.05)] bg-white z-10">
                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${encodeURIComponent('https://vision.ootentik.online/verify/' + result.id)}`} alt="QR" className="w-[60px] h-[60px] opacity-90" crossOrigin="anonymous" />
                            <span className="text-[6px] font-black uppercase tracking-[0.2em] text-zinc-400 mt-1">{getT('certificate', lang)}</span>
                        </div>
                    )}

                    {/* Header with Lang Switch */}
                    <div className="flex justify-between items-start">
                        <div>
                            <div className="px-2 py-0.5 bg-zinc-100 rounded text-[10px] font-bold text-zinc-500 uppercase tracking-widest inline-block mb-3">
                                {getT('analysisReport', lang)}
                            </div>
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h2 className="text-3xl font-black text-black">
                                        {result.visionScore}/100
                                    </h2>
                                    <p className={`text-xs font-bold uppercase tracking-widest mt-1 ${result.visionScore > 80 ? 'text-emerald-500' : result.visionScore > 60 ? 'text-orange-500' : 'text-red-500'}`}>
                                        {result.visionScore > 80 ? getT('authentic', lang) : result.visionScore > 60 ? getT('inconclusive', lang) : getT('counterfeitDetected', lang)}
                                    </p>
                                </div>
                                {getGradeBadge(result.rawAiResponse?.condition_grade)}
                            </div>
                            <p className="text-zinc-400 text-xs mt-2 uppercase tracking-widest font-semibold">Vision AI ID #{result.id.slice(-8).toUpperCase()}</p>
                        </div>
                        {/* Lang Switcher */}
                        <div className="flex bg-zinc-100/50 rounded-full p-1 border border-zinc-200/50 gap-1">
                            <button
                                onClick={() => setLang('FR')}
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${lang === 'FR' ? 'bg-white shadow text-black font-bold' : 'text-zinc-400 hover:text-black'}`}
                            >
                                FR
                            </button>
                            <button
                                onClick={() => setLang('EN')}
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${lang === 'EN' ? 'bg-white shadow text-black font-bold' : 'text-zinc-400 hover:text-black'}`}
                            >
                                EN
                            </button>
                        </div>
                    </div>

                    {/* Status Fields */}
                    <div className="space-y-4 pt-2">
                        <div className="flex justify-between items-center py-4 border-b border-zinc-100">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-zinc-50 flex items-center justify-center text-zinc-800">
                                    <Store className="w-5 h-5 opacity-80" />
                                </div>
                                <span className="text-zinc-500 text-sm font-semibold tracking-wide">{getT('authenticityScore', lang)}</span>
                            </div>
                            <span className={`text-xl font-bold ${result.visionScore > 80 ? 'text-black' : 'text-orange-500'}`}>
                                {result.visionScore}/100
                            </span>
                        </div>

                        <div className="flex justify-between items-center py-4 border-b border-zinc-100">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-zinc-50 flex items-center justify-center text-zinc-800">
                                    <Info className="w-5 h-5 opacity-80" />
                                </div>
                                <span className="text-zinc-500 text-sm font-semibold tracking-wide">{getT('aiReliability', lang)}</span>
                            </div>
                            <span className="text-black font-bold tracking-wider bg-zinc-100 px-3 py-1 rounded-md">{result.confidenceLevel}</span>
                        </div>
                    </div>

                    {/* Summary Box */}
                    <div className="bg-zinc-50 rounded-2xl p-5 border border-zinc-100 text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap font-medium">
                        {getT(result.globalAssessment, lang)}
                    </div>

                    {/* Red Flags if Any */}
                    {result.redFlags && result.redFlags.length > 0 && (
                        <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
                            <h4 className="text-red-600 font-extrabold text-sm mb-3 flex items-center gap-2 tracking-wide uppercase">
                                <AlertTriangle className="w-4 h-4" /> {getT('redFlags', lang)}
                            </h4>
                            <ul className="space-y-3">
                                {result.redFlags.map((flag: any, i: number) => (
                                    <li key={i} className="text-xs text-red-700 flex gap-3 font-medium leading-relaxed">
                                        <span className="mt-0.5 opacity-50">•</span>
                                        <span>{getT(flag.description, lang)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Receipt Analysis Alert */}
                    {result.rawAiResponse?.receipt_analysis && result.rawAiResponse.receipt_analysis !== 'N/A' && (
                        <div className="p-4 rounded-xl border border-blue-200 bg-blue-50/50 flex gap-4 mt-6">
                            <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                            <div>
                                <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-1">
                                    {getT('receiptAnalysis', lang)}
                                </h4>
                                <p className="text-sm font-semibold text-blue-800 leading-relaxed">
                                    {getT(result.rawAiResponse.receipt_analysis, lang)}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Market Value Box */}
                    {result.rawAiResponse?.estimated_market_value && (
                        <div className="bg-zinc-50 rounded-2xl p-5 border border-zinc-100 flex flex-col gap-2">
                            <span className="text-[10px] font-extrabold text-zinc-400 uppercase tracking-widest">{getT('estimatedMarketValue', lang)}</span>
                            <span className="text-[13px] font-black text-black">
                                {getT(result.rawAiResponse.estimated_market_value, lang)}
                            </span>
                        </div>
                    )}

                    {/* Legal Notice */}
                    {result.rawAiResponse?.final_notice && (
                        <div className="bg-zinc-50/50 rounded-2xl p-4 border border-zinc-100 flex items-start gap-3">
                            <Info className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                            <p className="text-[9px] text-zinc-500 font-bold leading-relaxed uppercase tracking-wider text-justify">
                                {getT(result.rawAiResponse.final_notice, lang)}
                            </p>
                        </div>
                    )}
                </div>

                {/* Financial Dashboard (Option 3 B2B Tool) */}
                </div> {/* End of reportRef wrapper */}

                {/* Financial Dashboard (Option 3 B2B Tool) - Kept OUTSIDE the PDF to hide margins from customers */}
                {(result.rawAiResponse?.market_value_min_eur || result.rawAiResponse?.market_value_max_eur) && (
                    <div className="bg-zinc-900 rounded-[2rem] p-6 shadow-xl border border-zinc-800 space-y-4 relative overflow-hidden mt-6">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 rounded-full blur-[80px] opacity-20 pointer-events-none"></div>
                        <h3 className="text-white font-black text-sm tracking-widest uppercase opacity-90">{getT('b2bValueEstimation', lang)}</h3>
                        
                        <div className="flex flex-col gap-3">
                            <div className="bg-zinc-800/50 p-4 rounded-2xl border border-zinc-700/50 flex justify-between items-center">
                                <span className="text-xs text-zinc-400 font-bold uppercase tracking-wider">{getT('marketValueB2C', lang)}</span>
                                <span className="text-lg font-black text-white">
                                    {result.rawAiResponse.market_value_min_eur} € - {result.rawAiResponse.market_value_max_eur} €
                                </span>
                            </div>
                            
                            {/* B2B Margin Calculation (Suggest buying at 60% of average market value) */}
                            {result.rawAiResponse.market_value_min_eur && result.rawAiResponse.market_value_max_eur && (
                                <div className="bg-emerald-950/30 p-4 rounded-2xl border border-emerald-900/30 flex justify-between items-center relative overflow-hidden">
                                    <div className="absolute left-0 top-0 h-full w-1 bg-emerald-500"></div>
                                    <span className="text-xs text-emerald-300 font-bold uppercase tracking-wider pl-2">{getT('suggestedBuyPrice', lang)}</span>
                                    <span className="text-2xl font-black text-emerald-400">
                                        {Math.round(((result.rawAiResponse.market_value_min_eur + result.rawAiResponse.market_value_max_eur) / 2) * 0.60)} €
                                    </span>
                                </div>
                            )}
                        </div>
                        <p className="text-[9px] text-zinc-500 font-bold tracking-wider mt-4 leading-relaxed uppercase">
                            * {getT('b2bConfidentialNotice', lang)}
                        </p>
                    </div>
                )}

                <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-zinc-100 flex flex-col gap-3">
                    <button
                        onClick={downloadPDF}
                        disabled={isGeneratingPdf}
                        className="w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-zinc-800 transition-all flex items-center justify-center gap-2 shadow-lg"
                    >
                        {isGeneratingPdf 
                          ? getT('generating', lang)
                          : getT('exportToPDF', lang)}
                    </button>
                    <button
                        onClick={() => { setStatus('IDLE'); setFiles([]); setResult(null); }}
                        className="w-full py-4 bg-transparent border-2 border-zinc-100 text-black rounded-xl font-extrabold hover:bg-zinc-50 transition-all flex items-center justify-center gap-2"
                    >
                        {getT('newAnalysis', lang)}
                    </button>
                </div>

                <div className="text-center text-[10px] text-zinc-400 pb-8 uppercase tracking-[0.2em] font-semibold">
                    OOTENTIK UNIVERS™ • AI ENGINE
                </div>

            </div>
        );
    }

    // Default Upload View (Clean & Light)
    return (
        <div className="w-full max-w-md mx-auto min-h-screen flex flex-col justify-center px-4 py-8 animate-in fade-in duration-700 bg-white text-black">

            <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onLoginSuccess={handleLoginSuccess} />

            {/* Minimalist Top Navigation - Fully Responsive */}
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2 sm:gap-4 z-50">
                <select 
                    value={lang}
                    onChange={(e) => setLang(e.target.value as any)}
                    className="bg-zinc-100/80 backdrop-blur text-zinc-700 font-bold text-[10px] sm:text-xs uppercase tracking-widest rounded-full pl-3 pr-2 py-1.5 sm:py-2 border border-zinc-200 outline-none cursor-pointer"
                >
                    <option value="FR">🇫🇷 FR</option>
                    <option value="EN">🇬🇧 EN</option>
                    <option value="JP">🇯🇵 JP</option>
                    <option value="CN">🇨🇳 CN</option>
                    <option value="IT">🇮🇹 IT</option>
                    <option value="ES">🇪🇸 ES</option>
                    <option value="KR">🇰🇷 KR</option>
                    <option value="AR">🇸🇦 AR</option>
                    <option value="PT">🇵🇹 PT</option>
                </select>
                {isLoggedIn && (
                    <>
                        <a href="/pricing" className="text-[9px] sm:text-[10px] font-black text-zinc-500 hover:text-black uppercase tracking-widest transition-colors">
                            {getT('pricing', lang)}
                        </a>
                        <a href="/account" className="text-[9px] sm:text-[10px] font-black text-black border border-zinc-200 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full hover:bg-zinc-50 transition-colors uppercase tracking-widest">
                            {getT('myAccount', lang)}
                        </a>
                    </>
                )}
            </div>

            <div className="text-center space-y-3 mb-12 relative z-10 pt-8">
                
                <h1 className="text-4xl font-black text-black tracking-tighter uppercase pt-2">
                    OOTENTIK<span className="font-light">VISION</span>
                </h1>
                
                <p className="text-zinc-500 text-[10px] tracking-widest uppercase px-8 font-bold">
                    High-End Authenticity Engine
                </p>
            </div>

            {status === 'ERROR' && (
                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm border border-red-100 text-center tracking-wide font-semibold">
                    {getT('systemErrorRetry', lang)}
                    <button onClick={() => setStatus('IDLE')} className="underline ml-2 font-bold hover:text-red-500">{getT('retry', lang)}</button>
                </div>
            )}

            {!isLoggedIn ? (
                <div className="bg-white rounded-[2rem] p-8 shadow-[0_4px_40px_rgba(0,0,0,0.04)] border border-zinc-100 flex flex-col items-center text-center space-y-8 relative overflow-hidden mt-4">
                    <div className="absolute -top-32 -right-32 w-64 h-64 bg-blue-50 rounded-full blur-[80px]"></div>
                    <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-zinc-50 rounded-full blur-[80px]"></div>
                    
                    <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center shadow-2xl shadow-black/20 text-white z-10">
                        <Camera className="w-8 h-8" />
                    </div>
                    
                    <div className="space-y-4 z-10 w-full">
                        <h2 className="text-2xl font-black text-black tracking-tighter">
                            {getT('landingTitle', lang)}
                        </h2>
                        <p className="text-sm font-semibold text-zinc-500 leading-relaxed max-w-sm mx-auto">
                            {getT('landingDesc', lang)}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 w-full gap-3 pt-4 border-t border-zinc-100 z-10">
                        <div className="flex items-center gap-3 bg-zinc-50 p-3 rounded-xl border border-zinc-200/60 transition-colors hover:border-zinc-300">
                            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center shrink-0"><ShieldCheck className="w-4 h-4 text-white"/></div>
                            <span className="text-xs font-bold text-zinc-700 text-left leading-tight">{getT('digitalCertTitle', lang)}</span>
                        </div>
                        <div className="flex items-center gap-3 bg-zinc-50 p-3 rounded-xl border border-zinc-200/60 transition-colors hover:border-zinc-300">
                            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center shrink-0"><ArrowRight className="w-4 h-4 text-white"/></div>
                            <span className="text-xs font-bold text-zinc-700 text-left leading-tight">{getT('b2bEstTitle', lang)}</span>
                        </div>
                    </div>

                    <button
                        onClick={() => setShowLoginModal(true)}
                        className="w-full py-5 bg-black text-white rounded-xl font-black text-[11px] uppercase tracking-[0.2em] shadow-xl shadow-zinc-300 hover:bg-zinc-800 hover:shadow-2xl hover:-translate-y-0.5 transition-all z-10 flex items-center justify-center gap-2"
                    >
                        {getT('openScanner', lang)}
                    </button>
                    
                    <a href="/pricing" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-black transition-colors z-10">
                        {getT('viewPricing', lang)}
                    </a>
                </div>
            ) : status === 'ANALYZING' ? (
                <div className="bg-white rounded-[2rem] p-10 shadow-[0_4px_40px_rgba(0,0,0,0.04)] border border-zinc-100 text-center space-y-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-black to-transparent opacity-10 animate-pulse"></div>
                    <div className="relative w-24 h-24 mx-auto">
                        <div className="absolute inset-0 rounded-full border border-zinc-100"></div>
                        <div className="absolute inset-0 rounded-full border border-t-black animate-[spin_1s_ease-in-out_infinite]"></div>
                        <div className="absolute inset-2 rounded-full border border-zinc-200 animate-[spin_2s_linear_infinite_reverse]"></div>
                    </div>
                    <div className="space-y-2">
                        <h3 className="text-lg font-black text-black tracking-widest uppercase">{getT('analyzing', lang)}</h3>
                        <p className="text-zinc-500 text-xs tracking-wide font-medium whitespace-pre-wrap">
                            {getT('analyzingDesc', lang)}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-[2rem] shadow-[0_4px_40px_rgba(0,0,0,0.04)] border border-zinc-100 overflow-hidden">
                    {/* Header Action & Guide */}
                    <div className="bg-zinc-50 border-b border-zinc-100 flex flex-col">
                        <div className="p-6 pb-4 text-center">
                            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-4">
                                {getT('categorySelect', lang)}
                            </p>
                            <div className="flex flex-wrap justify-center gap-2">
                                {(Object.keys(PHOTO_GUIDES) as Array<keyof typeof PHOTO_GUIDES>).map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setCategory(cat)}
                                        className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${category === cat ? 'bg-black text-white border-black shadow-md' : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300'}`}
                                    >
                                        {PHOTO_GUIDES[cat].icon} {getT(cat.toLowerCase(), lang)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="px-6 pb-6">
                            <div className="bg-white rounded-2xl p-4 border border-zinc-200/60 shadow-inner">
                                <h4 className="text-[10px] font-black text-zinc-800 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <Camera className="w-3 h-3 text-blue-500" />
                                    {getT('photoReq', lang)}
                                </h4>
                                <ul className="space-y-2">
                                    {PHOTO_GUIDES[category].steps.map((step, idx) => (
                                        <li key={idx} className="text-[11px] text-zinc-600 font-semibold flex items-center gap-2">
                                            <span className="w-4 h-4 rounded-full bg-zinc-100 text-zinc-400 flex items-center justify-center text-[9px] shrink-0">{idx + 1}</span>
                                            {(step as any)[lang] || step['EN']}
                                        </li>
                                    ))}
                                    <li className="text-[11px] text-blue-600/80 font-bold flex items-center gap-2 pt-1">
                                        <span className="w-4 h-4 rounded-full bg-blue-50 text-blue-400 flex items-center justify-center text-[9px] shrink-0 font-black">+</span>
                                        {getT('receiptOpt', lang)}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 pb-2 space-y-4 border-b border-zinc-100 bg-white relative z-10 w-full">
                        <div className="text-center">
                            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest leading-relaxed">
                                {getT('modelOpt', lang)}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                placeholder={getT('brandEx', lang)}
                                value={brandName}
                                onChange={(e) => setBrandName(e.target.value)}
                                className="w-1/2 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-xs font-bold text-black placeholder:text-zinc-400 focus:outline-none focus:border-black transition-colors"
                            />
                            <input 
                                type="text" 
                                placeholder={getT('modelEx', lang)}
                                value={modelName}
                                onChange={(e) => setModelName(e.target.value)}
                                className="w-1/2 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-xs font-bold text-black placeholder:text-zinc-400 focus:outline-none focus:border-black transition-colors"
                            />
                        </div>
                        {userProfile && (userProfile.subscriptionPlan === 'PRO' || userProfile.subscriptionPlan === 'VIP' || userProfile.role === 'ADMIN') && category === 'WATCH' && (
                            <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-xl p-4 flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="watchRegister"
                                    checked={checkWatchRegister}
                                    onChange={(e) => setCheckWatchRegister(e.target.checked)}
                                    disabled={userProfile.role !== 'ADMIN' && userProfile.watchRegisterUsed >= userProfile.watchRegisterQuota}
                                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                                />
                                <div className="flex flex-col">
                                    <label htmlFor="watchRegister" className={`text-xs font-bold cursor-pointer ${userProfile.role !== 'ADMIN' && userProfile.watchRegisterUsed >= userProfile.watchRegisterQuota ? 'text-zinc-400' : 'text-black'}`}>
                                        Vérifier sur The Watch Register
                                    </label>
                                    <span className="text-[10px] text-zinc-500 font-semibold mt-1">
                                        {userProfile.role === 'ADMIN' 
                                            ? "Accès Admin illimité"
                                            : `Il vous reste ${Math.max(0, userProfile.watchRegisterQuota - userProfile.watchRegisterUsed)} / ${userProfile.watchRegisterQuota} vérifications ce mois-ci.`}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-6 space-y-6 bg-white relative z-0 w-full">
                        <div className="text-center">
                            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">{getT('importImg', lang)}</p>
                        </div>
                        {/* Buttons Area */}
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                onClick={() => {
                                    if (!isLoggedIn) {
                                        setShowLoginModal(true);
                                    } else {
                                        cameraInputRef.current?.click();
                                    }
                                }}
                                className="aspect-[4/3] flex flex-col items-center justify-center gap-3 bg-zinc-50 hover:bg-zinc-100 transition-all rounded-2xl border border-zinc-100 group"
                            >
                                <Camera className="w-8 h-8 text-black group-hover:scale-110 transition-transform" />
                                <span className="text-[10px] font-black text-black uppercase tracking-widest">{getT('cameraBtn', lang)}</span>
                            </button>
                            <input type="file" ref={cameraInputRef} accept="image/*" capture="environment" className="hidden" onChange={handleFiles} />

                            <button
                                onClick={() => {
                                    if (!isLoggedIn) {
                                        setShowLoginModal(true);
                                    } else {
                                        fileInputRef.current?.click();
                                    }
                                }}
                                className="aspect-[4/3] flex flex-col items-center justify-center gap-3 bg-zinc-50 hover:bg-zinc-100 transition-all rounded-2xl border border-zinc-100 group"
                            >
                                <Upload className="w-8 h-8 text-black group-hover:-translate-y-1 transition-transform" />
                                <span className="text-[10px] font-black text-black uppercase tracking-widest">{getT('filesBtn', lang)}</span>
                            </button>
                            <input type="file" ref={fileInputRef} multiple accept="image/*" className="hidden" onChange={handleFiles} />
                        </div>

                        {/* Preview List */}
                        {files.length > 0 && (
                            <div className="space-y-3 pt-2">
                                <div className="flex justify-between items-end">
                                    <h4 className="text-[10px] tracking-widest font-extrabold text-black uppercase">{getT('preview', lang)} ({files.length})</h4>
                                    <button onClick={() => setFiles([])} className="text-[10px] text-red-500 font-extrabold uppercase tracking-wider hover:text-red-600">{getT('clear', lang)}</button>
                                </div>
                                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                    {files.map((file, i) => (
                                        <div key={i} className="relative w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden border border-zinc-200 opacity-90 hover:opacity-100 transition-opacity">
                                            <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" alt="thumb" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Main Action */}
                        <button
                            onClick={runAnalysis}
                            disabled={files.length < 5}
                            className="w-full py-5 bg-black hover:bg-zinc-800 text-white rounded-xl font-black shadow-lg shadow-zinc-300 disabled:opacity-30 disabled:shadow-none disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 uppercase tracking-widest text-[10px]"
                        >
                            {files.length < 5 
                                ? getT('moreRequired', lang).replace('{x}', String(5 - files.length))
                                : files.length >= 10 
                                    ? getT('startAiMax', lang) 
                                    : getT('startAi', lang)}
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-12 flex justify-center gap-6 text-[10px] text-zinc-400 uppercase tracking-widest font-black flex-wrap">
                <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {getT('encrypted', lang)}</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {getT('privateData', lang)}</span>
            </div>
        </div>
    );
}
